import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Trash } from '@phosphor-icons/react'

import { AvatarLetters, Button, ComboBox, InputWithLabel, ToastContext, UploadImage } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import AppContext from '@/common/context/app.context'
import DeleteUserConfirmationModal from '@/configuration/components/DeleteUserConfirmationModal/DeleteUserConfirmationModal'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'
import routes from '@/Router/routes.constants'

import useUserDetail from './hooks/useUserDetail'
import UserDetailConfigurationSkeleton from './UserDetailConfigurationSkeleton/UserDetailConfigurationSkeleton'
import { formFieldsId } from './constants'
import { getPayload, validateUsername } from './helpers'
import * as Styles from './styles'

const UserDetailConfiguration = () => {
  const { id } = useParams()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const navigation = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()

  const [showDeleteUserConfirmationModal, setShowDeleteUserConfirmationModal] = useState(false)
  const [usernameError, setUsernameError] = useState(false)

  const {
    image,
    name,
    setName,
    lastName,
    setLastName,
    username,
    setUsername,
    email,
    setEmail,
    group,
    setGroup,
    changeImage,
    deleteImage,
    isOwner,
    hasChanges,
    setHasChanges,
    revertChanges,
    createUser,
    editUser,
    editingUser,
    availableGroups,
    deleteUser,
    deletingUser,
    loadingUser,
    otherStoresGroups,
    loadingGroups,
    errorField,
    setErrorField,
    errorMessage,
    setErrorMessage,
  } = useUserDetail({
    storeId: info?.selectedStore?.id,
    userId: id,
    selectedGroupId: location.state?.selectedGroupId,
  })

  const onSave = async () => {
    const payload = getPayload({ id, name, lastName, username, email, image, group, otherStoresGroups })

    if (id === 'new') {
      try {
        await createUser({ storeId: info.selectedStore.id, user: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        setHasChanges(false)
        navigation(routes.configurationPage.users)
      } catch (error) {
        setErrorMessage(error.errorMessage)
        focusOnError(error.response?.data, setErrorField)
      }
    } else {
      try {
        await editUser({ storeId: info.selectedStore.id, user: payload, userId: id })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        setHasChanges(false)
        navigation(routes.configurationPage.users)
      } catch (error) {
        setErrorMessage(error.errorMessage)
        focusOnError(error.response?.data, setErrorField)
      }
    }
  }

  const onDelete = async () => {
    try {
      await deleteUser({ storeId: info.selectedStore.id, userId: id })
      setToastInfo({ show: true, message: t('Usuario eliminado'), type: 'success' })
      navigation(routes.configurationPage.users)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleUsername = event => {
    const newUsername = event.target.value
    setUsername(newUsername)
    setUsernameError(!validateUsername(newUsername))
  }

  const getEmailHelperText = () => {
    if (isOwner) {
      return t('No es posible cambiar el email asociado al dueño de la tienda')
    }

    if (errorField === formFieldsId.email) return errorMessage
  }

  const isFormValid = () => email && group && lastName && username && !usernameError

  if (loadingUser || loadingGroups) {
    return <UserDetailConfigurationSkeleton />
  }

  return (
    <>
      <Styles.Container>
        {hasChanges && <UnsavedChangesAlert />}
        <Styles.UserImageContainer>
          {image?.preview ? (
            <img src={image?.preview} alt={name} />
          ) : (
            <AvatarLetters id={id} firstName={name} lastName={lastName} />
          )}
          <UploadImage
            label={t('Imagen de perfil')}
            recommendation={t('Max 1MB. Formatos: JPG, JPEG, PNG, HEIC.')}
            onChange={file => changeImage(file)}
            onDelete={() => deleteImage()}
            preview={image?.preview}
          />
        </Styles.UserImageContainer>
        <Styles.UserInfoContainer>
          <Styles.InputsContainer>
            <InputWithLabel
              label={t('Nombre')}
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
              inputProps={{
                id: formFieldsId.name,
              }}
              error={errorField === formFieldsId.name}
              helperText={errorField === formFieldsId.name && errorMessage}
            />
            <InputWithLabel
              label={t('Apellidos')}
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              fullWidth
              inputProps={{
                id: formFieldsId.lastName,
              }}
              error={errorField === formFieldsId.lastName}
              helperText={errorField === formFieldsId.lastName && errorMessage}
            />
          </Styles.InputsContainer>
          <Styles.InputsContainer>
            <InputWithLabel
              label={t('Alias')}
              value={username}
              onChange={handleUsername}
              error={usernameError || errorField === formFieldsId.username}
              fullWidth
              inputProps={{
                id: formFieldsId.username,
              }}
              helperText={errorField === formFieldsId.username && errorMessage}
            />
            <InputWithLabel
              label={t('Correo electrónico')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              error={errorField === formFieldsId.email}
              helperText={getEmailHelperText()}
              inputProps={{
                id: formFieldsId.email,
              }}
              disabled={isOwner}
            />
          </Styles.InputsContainer>
          <ComboBox
            label={t('Grupo')}
            menuItems={availableGroups}
            value={group}
            handleChange={e => setGroup(e.target.value)}
            disabled={isOwner}
            helperText={isOwner && t('No es posible cambiar el grupo del dueño de la tienda')}
          />
        </Styles.UserInfoContainer>
        <Styles.ActionsContainer>
          <SaveChangesButtons
            hasChanges={hasChanges}
            onSave={onSave}
            onRevertChanges={revertChanges}
            loading={editingUser}
            disabled={editingUser || !isFormValid()}
          />
          {id !== 'new' && !isOwner && (
            <Button
              color="danger"
              variant="text"
              onClick={() => setShowDeleteUserConfirmationModal(true)}
              loading={deletingUser}
              disabled={deletingUser}
            >
              <Trash size={24} />
              {t('Eliminar usuario')}
            </Button>
          )}
        </Styles.ActionsContainer>
      </Styles.Container>
      <DeleteUserConfirmationModal
        show={showDeleteUserConfirmationModal}
        onClose={() => setShowDeleteUserConfirmationModal(false)}
        onConfirm={onDelete}
        userName={name}
        storeName={info?.selectedStore?.name}
        confirming={deletingUser}
      />
    </>
  )
}

export default UserDetailConfiguration
